import { useEffect } from 'react'
import { logger } from '@upadelmatch/core'
import { authRepository } from './api/auth-repository'
import { useAuthStore } from '@/core/auth/auth-store'
import { platformAdminRepository } from './api/platform-admin-repository'
import { setAnalyticsUser, setAnalyticsUserProperties } from '@/core/analytics'

// Subscribe to Firebase auth state once at app root and mirror it into the
// store. Admin resolution = the clubmanager mechanism (2026-07-04): membership
// in `platform_config/admins.emails` AND a verified email — the emailVerified
// check blocks email-spoofing via direct Auth REST signup, and phone-only
// accounts (email == null) short-circuit to non-admin without a read. The
// repository retries across the token-hydration race and fails closed, so the
// store is only written once the answer is real (guards render nothing while
// status is 'initial' — no admin mis-route on first paint).
export function useAuthListener(): void {
  const setAuth = useAuthStore((s) => s.setAuth)
  useEffect(() => {
    return authRepository.observe((user) => {
      if (!user) {
        setAuth(null, false)
        setAnalyticsUser(null)
        return
      }
      setAnalyticsUser(user.uid)
      // Coarse session attributes, exported to BigQuery as `user_properties` on
      // every event. `is_guest` is the one that matters most here: it separates
      // a share-door visitor from a real player on EVERY metric, without adding
      // a param to each event.
      setAnalyticsUserProperties({ isGuest: user.isAnonymous })
      // `observe` wants a void callback, so the admin lookup cannot be awaited
      // here — and an unhandled rejection would leave the store on 'initial',
      // which the guards render as NOTHING. That is a permanently blank app,
      // not a degraded one. `isAdmin` already fails closed internally, but the
      // callback must not depend on that staying true: catch here and seat the
      // session as non-admin, so auth always resolves to a rendered state.
      platformAdminRepository
        .isAdmin(user.email)
        .then((inAdminList) => setAuth(user, inAdminList && user.emailVerified))
        .catch((e: unknown) => {
          logger.e('[auth] admin resolution failed — seating session as non-admin', e)
          setAuth(user, false)
        })
    })
  }, [setAuth])
}
